import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import FeaturedRoutePromotion from '../components/FeaturedRoutePromotion';
import SeoHead from '../components/SeoHead';
import {
  VEHICLES,
  DRIVER_BATA,
  MIN_ONEWAY_KM,
  getVehiclePrice,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import { getRouteMetrics } from '../services/routeMetrics';
import '../styles/RouteLanding.css';

const ROUTE_PATH = '/madurai-to-chennai-taxi';
const DEFAULT_PICKUP = 'Madurai';
const DEFAULT_DROP = 'Chennai';
const BOOKING_ID = 'route-booking';

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const faqs = [
  {
    question: 'How can I book a Madurai to Chennai taxi?',
    answer:
      'Open the booking card on this page. Pickup is already set to Madurai and drop to Chennai. Add your mobile number, tap Get Fare Estimate, then choose a vehicle, date and time. Confirming the review sends the request through the same Easy Drop Taxi booking process used on the homepage.',
  },
  {
    question: 'Can I book a one-way cab from Madurai to Chennai?',
    answer:
      'Yes. This page defaults to One Way, so you can travel to Chennai without arranging a return taxi. Switch to Round Trip in the booking card if you want the vehicle for both directions.',
  },
  {
    question: 'Which vehicles can I use for this trip?',
    answer:
      'Sedan, SUV and Crysta are the options already available in the Easy Drop Taxi booking system. After you search, each card shows the estimate from the published per-kilometre rate plus driver bata.',
  },
  {
    question: 'Can I start from a specific place in Madurai?',
    answer:
      'Yes. The pickup field starts as Madurai, but you can change it to a house, hotel, station or any address Google Places suggests.',
  },
  {
    question: 'Can I choose when I leave Madurai?',
    answer:
      'Yes. Pickup date and time are collected in the second booking step, after the fare estimate, along with your name and vehicle choice.',
  },
  {
    question: 'How is the Madurai to Chennai fare worked out?',
    answer:
      `The booking form uses Easy Drop Taxi’s existing formula: billed distance × vehicle rate + ₹${DRIVER_BATA} driver bata. One-way trips have a ${MIN_ONEWAY_KM} km minimum. Toll, state permit, hill station, parking and waiting-time charges are extra, as shown on the review screen.`,
  },
  {
    question: 'Can I book from Madurai Airport to Chennai?',
    answer:
      'Yes. Change pickup to Madurai Airport or another airport address, keep Chennai as the drop or set a more specific Chennai point such as Chennai Airport, then continue with the same estimate and booking steps.',
  },
];

const MaduraiToChennai = () => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [routePrefill, setRoutePrefill] = useState(null);
  const [routeMetrics, setRouteMetrics] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadMetrics = async () => {
      const metrics = await getRouteMetrics(DEFAULT_PICKUP, DEFAULT_DROP);
      if (!cancelled && metrics) setRouteMetrics(metrics);
    };

    if (window.google) {
      loadMetrics();
    } else {
      window.addEventListener('google-maps-ready', loadMetrics, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('google-maps-ready', loadMetrics);
    };
  }, []);

  const jsonLd = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://easydroptaxi.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Madurai to Chennai Taxi',
          item: `https://easydroptaxi.com${ROUTE_PATH}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Madurai to Chennai Taxi',
      serviceType: 'One-way taxi booking',
      provider: {
        '@type': 'TaxiService',
        name: 'Easy Drop Taxi',
        url: 'https://easydroptaxi.com/',
        telephone: '+919629954544',
      },
      areaServed: ['Madurai', 'Chennai', 'Tamil Nadu'],
      url: `https://easydroptaxi.com${ROUTE_PATH}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ]), []);

  const handleSelectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    scrollToBooking();
  };

  return (
    <main className="route-landing">
      <SeoHead
        title="Madurai to Chennai Taxi | One Way Cab Booking | Easy Drop Taxi"
        description="Book a one-way taxi from Madurai to Chennai with Easy Drop Taxi. Enter your pickup in Madurai, choose a vehicle and get a fare estimate through the same online booking form."
        path={ROUTE_PATH}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>Madurai to Chennai Taxi</span>
            </nav>
            <h1>Madurai to Chennai Taxi</h1>
            <p className="rl-hero-lead">
              Start in Madurai and travel door to door toward Chennai with a one-way Easy Drop Taxi booking.
            </p>
            <div className="rl-hero-note">
              <span><i className="fa fa-road" aria-hidden="true"></i>One Way Taxi</span>
              <span><i className="fa fa-phone" aria-hidden="true"></i><a href="tel:+919629954544">+91 96299 54544</a></span>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              initialPickup={routePrefill?.pickup || DEFAULT_PICKUP}
              initialDrop={routePrefill?.drop || DEFAULT_DROP}
              initialTripType="One Way"
              initialVehicle={preferredVehicle}
              searchButtonLabel="Get Fare Estimate"
            />
          </div>
        </div>
      </section>

      <section className="rl-strip" aria-label="Route information">
        <div className="container">
          <div className="rl-strip-grid">
            <div className="rl-strip-item">
              <span className="rl-strip-label">Route</span>
              <span className="rl-strip-value">Madurai → Chennai</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Trip Type</span>
              <span className="rl-strip-value">One Way Taxi</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Approx. Distance</span>
              <span className="rl-strip-value">
                {routeMetrics?.distanceKm
                  ? `${routeMetrics.distanceKm} km`
                  : 'Shown after fare estimate'}
              </span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Approx. Travel Time</span>
              <span className="rl-strip-value">
                {routeMetrics?.durationText || 'Calculated for your pickup'}
              </span>
            </div>
          </div>
        </div>
      </section>

      <FeaturedRoutePromotion
        origin={DEFAULT_PICKUP}
        destination={DEFAULT_DROP}
        onGetFareEstimate={({ origin, destination, vehicleType }) => {
          setRoutePrefill({ pickup: origin, drop: destination });
          setPreferredVehicle(vehicleType);
          const target = document.getElementById(BOOKING_ID);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Choose Your Taxi</h2>
          <p className="rl-section-intro">
            Select from the same Sedan, SUV and Crysta fleet used across Easy Drop Taxi. The rates below are the published per-kilometre tariff, not a separate Madurai package.
          </p>
          <div className="rl-vehicle-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-vehicle-card">
                <div className="rl-vehicle-media">
                  <img
                    src={vehicle.fleetImage || vehicle.image}
                    alt={`${getVehicleDisplayName(vehicle)} taxi for travel from Madurai toward Chennai`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="rl-vehicle-body">
                  <h3>{getVehicleDisplayName(vehicle)}</h3>
                  <p>{vehicle.description}</p>
                  <div className="rl-vehicle-meta">
                    <span><i className="fa fa-users" aria-hidden="true"></i>{vehicle.seats}</span>
                    <span><i className="fa fa-tag" aria-hidden="true"></i>₹{vehicle.rate}/km</span>
                  </div>
                  {routeMetrics?.distanceKm ? (
                    <p>
                      Estimated one-way fare: ₹
                      {getVehiclePrice(routeMetrics.distanceKm, vehicle.rate, 'One Way').toLocaleString('en-IN')}
                    </p>
                  ) : (
                    <p>Use Get Fare Estimate in the booking card for your exact total.</p>
                  )}
                  <button
                    type="button"
                    className={`rl-select-btn${preferredVehicle === vehicle.type ? ' is-selected' : ''}`}
                    onClick={() => handleSelectVehicle(vehicle.type)}
                  >
                    {preferredVehicle === vehicle.type ? 'Selected' : 'Select'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Madurai to Chennai Taxi</h2>
          <div className="rl-prose">
            <p>
              Leaving Madurai for Chennai is often a one-direction trip: a return after temple or family time, a connection to the capital, or a transfer toward the airport. Easy Drop Taxi lets you book that outstation cab online instead of coordinating a separate return vehicle in Madurai.
            </p>
            <p>
              Set a pickup that matches where you actually start — a home, hotel or another point in the city — and a Chennai drop for work, residence or onward travel. The form is the same one used for family, business and personal bookings, so there is no second process for this direction.
            </p>
            <p>
              After you request an estimate, add the date and time you want to leave. Confirmation goes through the regular Easy Drop Taxi flow, and the team follows up on the request once you submit it.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Why Choose EasyDropTaxi</h2>
          <div className="rl-benefit-grid">
            <article className="rl-benefit-card">
              <i className="fa fa-laptop" aria-hidden="true"></i>
              <h3>Easy Online Booking</h3>
              <p>Madurai pickup and Chennai drop are pre-filled. You still complete the rest of the trip in the same homepage booking form.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-exchange" aria-hidden="true"></i>
              <h3>One-Way Taxi Service</h3>
              <p>Travel north from Madurai without paying for an unused return, or switch the form to Round Trip if you need both legs.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-calculator" aria-hidden="true"></i>
              <h3>Transparent Fare Calculation</h3>
              <p>The estimate uses billed distance, the published vehicle rate and driver bata, with extras listed when you review.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-car" aria-hidden="true"></i>
              <h3>Multiple Vehicle Options</h3>
              <p>Sedan, SUV and Crysta are offered at the existing tariff so you can match seats to your group.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-map-marker" aria-hidden="true"></i>
              <h3>Door-to-Door Pickup &amp; Drop</h3>
              <p>Start from a Madurai address and finish at a Chennai address you type yourself, rather than a single stand.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-phone" aria-hidden="true"></i>
              <h3>Customer Support</h3>
              <p>Call <a href="tel:+919629954544">+91 96299 54544</a> if you need help before you confirm. Support is listed as 24/7 on the main site.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-user" aria-hidden="true"></i>
              <h3>Experienced Drivers</h3>
              <p>Outstation trips are assigned through Easy Drop Taxi’s existing driver network, not a separate reverse-route operator.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-snowflake-o" aria-hidden="true"></i>
              <h3>Fully A/C Taxis</h3>
              <p>The website fleet is listed as air-conditioned, which matters on a long intercity run toward Chennai.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Popular Pickup Locations in Madurai</h2>
          <p className="rl-section-intro">
            Madurai is in Easy Drop Taxi’s city list. You can type a more specific start point in the pickup field. The names below are common places travellers enter; they are not a guaranteed dedicated coverage list.
          </p>
          <div className="rl-place-grid">
            {['Madurai Airport', 'Madurai Junction', 'Mattuthavani', 'Madurai city areas', 'Major hotels'].map((place) => (
              <article key={place} className="rl-place-card">
                <h3>{place}</h3>
                <p>Use this as your pickup if it is where you will wait, then continue with the fare estimate.</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Popular Drop Locations in Chennai</h2>
          <p className="rl-section-intro">
            Chennai and Chennai Airport appear in the existing service city list. Drop-off stays editable, so you can name an airport, neighbourhood or hotel. Treat the examples as typical destinations you can type, not a separate catalogue.
          </p>
          <div className="rl-place-grid">
            {['Chennai Airport', 'Central Chennai', 'Tambaram', 'Porur', 'Anna Nagar', 'Velachery', 'OMR'].map((place) => (
              <article key={place} className="rl-place-card">
                <h3>{place}</h3>
                <p>Set this in the drop field if it is your Chennai destination before you request an estimate.</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>How to Book</h2>
          <div className="rl-step-grid">
            <article className="rl-step-card">
              <span className="rl-step-number">1</span>
              <h3>Enter Your Trip Details</h3>
              <p>Keep or edit Madurai pickup and Chennai drop, add your 10-digit mobile number, then tap Get Fare Estimate.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">2</span>
              <h3>Select Your Vehicle</h3>
              <p>Choose Sedan, SUV or Crysta, then add the date and time you want to leave Madurai and your name.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">3</span>
              <h3>Confirm Your Booking</h3>
              <p>Review the trip and fare, then confirm. The request uses the existing Easy Drop Taxi booking system.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Fare &amp; Pricing</h2>
          <p className="rl-section-intro">
            There is no fixed Madurai–Chennai package price. The total comes from the same calculation used on every Easy Drop Taxi booking.
          </p>
          <div className="rl-fare-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-fare-card">
                <h3>{getVehicleDisplayName(vehicle)}</h3>
                <p>₹{vehicle.rate} per km</p>
                <p>Driver bata: ₹{DRIVER_BATA}</p>
                {routeMetrics?.distanceKm ? (
                  <p>
                    Sample one-way estimate: ₹
                    {getVehiclePrice(routeMetrics.distanceKm, vehicle.rate, 'One Way').toLocaleString('en-IN')}
                  </p>
                ) : (
                  <p>Open the booking card to generate a live estimate for your addresses.</p>
                )}
              </article>
            ))}
          </div>
          <p className="rl-fare-note">
            One-way billing uses a {MIN_ONEWAY_KM} km minimum. Toll gate, state permit, hill station, parking and waiting time are extra, matching the note shown when you review a booking.
          </p>
          <button type="button" className="rl-select-btn" style={{ maxWidth: 280 }} onClick={scrollToBooking}>
            Get Fare Estimate
          </button>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Travel from Madurai to Chennai</h2>
          <div className="rl-prose">
            <p>
              This is a northbound intercity taxi. You board in Madurai and stay in the same vehicle until the Chennai drop you entered, without a mid-journey change at a bus stand or railway station.
            </p>
            <p>
              It works for a solo traveller, a couple or a family, depending on the vehicle. One-way booking means you do not have to find a second cab in Chennai unless you later want a round trip.
            </p>
            <p>
              Distance and time on this page come from the same mapping tools used to estimate the fare. They can change with your exact Madurai start point, Chennai drop and traffic, so treat them as a guide until you run the estimate.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Related Pages</h2>
          <p className="rl-section-intro">
            Need the opposite direction, or a general Easy Drop Taxi page? These routes already exist.
          </p>
          <div className="rl-links">
            <Link to="/chennai-to-madurai-taxi">Chennai to Madurai Taxi</Link>
            <Link to="/madurai-taxi">Madurai Taxi</Link>
            <Link to="/">Home</Link>
            <Link to="/tariff">Tariff</Link>
            <Link to="/services">Services</Link>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>Ready to Leave Madurai for Chennai?</h2>
          <p>Book your Madurai to Chennai taxi with EasyDropTaxi.</p>
          <button type="button" className="rl-cta-btn" onClick={scrollToBooking}>
            Book Your Taxi
          </button>
        </div>
      </section>
    </main>
  );
};

export default MaduraiToChennai;
